import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import * as Sentry from '@sentry/react'
import Btn from 'components/buttons/Btn'
import EmptyMessage from 'components/EmptyMessage'
import { BtnDesc, FileInputBtn } from 'components/form'
import Screen from 'components/layout/Screen'
import SpinnerFull from 'components/SpinnerFull'
import { useEffect, useRef, useState } from 'react'
import { ButtonGroup, Card, CardColumns, Col, Form, ListGroup, Row } from 'react-bootstrap'
import { Trans, useTranslation } from 'react-i18next'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import API from 'state/API'
import { useUser } from 'state/Auth'
import { PRIVILEGES } from 'state/Constants'
import { deleteImage, fetchItem, getCurrentItem, uploadImage } from 'state/Items'
import { getLoading } from 'state/Request'
import { Alerts, dataURIToBinary, useCurrentEntry } from 'utils'
import { canvasPreview } from './ImageCanvas/canvasPreview'

import './styles.scss'

const IMAGE_RATIO = Math.min(window.innerWidth*0.76, 360) // This calculates the exact width of the canvas discounting the margins and, if it reaches 360, wich is a value we saw that looks good at bigger screens, it just uses 360.

export default function ItemImagePage() {
	const { id }   = useParams()
	const dispatch = useDispatch()
	const { t }    = useTranslation()

	//cropper state vars
	const previewCanvasRef                  = useRef(null)
	const imgRef                            = useRef()
	const [crop, setCrop]                   = useState() // Sets the cropper configs, like width and height
	const [scale, setScale]                 = useState(1) // Scale of the image being cropped
	const [rotate, setRotate]               = useState(0) // Rotation of the image being cropped
	const [imgSrc, setImgSrc]               = useState('') // Uploaded image source
	const [cropped, setCropped]             = useState(false) // State variable that sets screen in ""cropping mode""
	const [newHeight, setNewHeight]         = useState(0) // The new image height if its naturalHeight is greater than 360
	const [newWidth, setNewWidth]           = useState(0) // The new image width if its naturalWidth is greater than 360
	const [termsAccepted, setTermsAccepted] = useState(false)

	/** @var {Item} item */
	const item                            = useCurrentEntry(getCurrentItem, fetchItem)
	const loading                         = useSelector(getLoading(API.RESOURCES.ITEMS))
	const [currentImage, setCurrentImage] = useState(item?.image || null)

	useEffect(() => {
		if (item && (currentImage !== item.image)) {
			setCurrentImage(item.image)
		}
	}, [item, currentImage, setCurrentImage])

	//must stay here, after the other hooks - which can't be optional zzzzzz
	const notAllowed = useUser().useFullPagePrivilege(PRIVILEGES().MENU_UPDATE, { page: 'Menu Item Image', id })
	if (notAllowed) {
		return notAllowed
	}

	async function onSubmit() {
		setCropped(false)
		setImgSrc('')
		const newImage = previewCanvasRef.current.toDataURL('image/jpeg')
		await dispatch(uploadImage(id, newImage))
		Alerts.success(t('This item\'s image got updated'))
		attachInSentry(false, false)
	}

	async function onDelete() {
		await dispatch(deleteImage(id)) //TODO briefly causes a 404 in console because the non-existing image gets reloaded on some rerender... but since it doesn't cause a UI issue, whatever? just using setCurrentImage(null) conflicts with the item state change in Redux
		Alerts.success(t('This item\'s image got deleted'))
	}

	/**
	 * Pass all false to clear the attachments
	 * @param data {false|string|Uint8Array}
	 * @param contentType {false|string}
	 * @see https://docs.sentry.io/platforms/javascript/guides/react/enriching-events/attachments/
	 */
	function attachInSentry(data, contentType) {
		Sentry.configureScope(scope => {
			if (!data && !contentType) {
				scope.clearAttachments()
			} else {
				const binary   = data instanceof Uint8Array? data : dataURIToBinary(data)
				const rand     = Math.random().toString().substring(2)
				const filename = `item_image_upload-${rand}.jpg` //fixed as JPG since that's the only allowed type now
				scope.addAttachment({ filename, data: binary, contentType })
			}
		})
	}

	//region: Cropper functions
	function _processImage(dataUrl, fileType) {
		attachInSentry(dataUrl, fileType)

		const img = new Image()
		img.onload = () => {
			if (img.naturalHeight >= IMAGE_RATIO || img.naturalWidth >= IMAGE_RATIO) { // Check for appropriate sizes
				const ratio = Math.min(IMAGE_RATIO / img.naturalWidth, IMAGE_RATIO / img.naturalHeight) // If it doesn't, it calculates the ratio to scale down
				setNewWidth(img.naturalWidth * ratio)
				setNewHeight(img.naturalHeight * ratio)
			} else {
				setNewWidth(img.naturalWidth) // If it does, just sets them at the state
				setNewHeight(img.naturalHeight)
			}
		}
		img.src = dataUrl
		setImgSrc(dataUrl)
	}
	/**
	 * @param event {string|{ target: { files: Blob[] }}} Either a Base64 string or the event from an HTML file picker
	 * @returns {false|void} False if error
	 */
	function onSelectFile(event) {
		let isDataUrl, isPresent, fileType
		if (typeof event == 'string') {
			isDataUrl = true
			isPresent = event.length
			fileType  = /^data:(\w+\/\w+);/.exec(event)[1] || 'unknown'
		} else {
			isDataUrl = false
			isPresent = event.target.files?.length
			fileType  = event.target.files[0].type
		}

		if (!isPresent) {
			return false //no need for a warning, this usually happens when the user hit "Cancel"
		}
		if (!['image/jpeg', 'image/jpg'].includes(fileType)) { //if this changes, attachInSentry() needs a fix too
			Alerts.warning(t('We only accept JPG pictures at the time.'))
			console.warn(`Received file is ${event.target.files[0].type}`, event.target.files[0])
			event.target.value = '' //cleanup the file field, if any
			return false
		}

		//resets all the state variables that the cropper uses and starts to work on the new image
		setCropped(false)
		setImgSrc('')
		setScale(1)
		setRotate(0)
		setCrop({
			unit  : '%',
			height: 100,
			width : 80,
			x     : 10,
			y     : 0,
		})

		if (isDataUrl) {
			_processImage(event, fileType)
		} else {
			const reader = new FileReader()
			reader.onloadend = () => _processImage(reader.result.toString(), fileType)
			reader.readAsDataURL(event.target.files[0])
			event.target.value = '' //cleanup the file field once we're done here
		}
	}

	function doCrop() {
		setCropped(true) // Sets the screen on "finished cropping mode"
		const realValue = { width: newWidth, height: newHeight }

		if (crop.unit === '%') { // Here it converts the unit that the cropper is using to pixels (The cropper uses % to work on the screen and "px" is a much easier unit to work with when we are re-scalling it)
			crop.unit   = 'px'
			crop.height = imgRef.current.height * crop.height / 100
			crop.width  = imgRef.current.width * crop.width / 100
			crop.x      = imgRef.current.width * crop.x / 100
			crop.y      = imgRef.current.height * crop.y / 100
		}
		// And here created the canvas with the cropping result, it uses realValue to scale down the canvas to show the result correctly
		canvasPreview(imgRef.current, realValue, previewCanvasRef.current, crop, scale, rotate)
	}
	//endregion

	return <Screen back title={item? t('Image: {{item}}', { item: item.display_name }) : t('Loading item details...')}>
		<Screen.Content className="itemImage" addPadding={!!item}>
			{!item?
				<SpinnerFull icon="image" resource={API.RESOURCES.ITEMS}/>
				:
				<CardColumns>
					<Card>
						<Card.Header><Icon icon="camera"/> {t('Image selection')}</Card.Header>
						<ListGroup variant="flush">
							<ListGroup.Item>
								<Card.Subtitle>{t('Current item description')}:</Card.Subtitle>
								<Card.Text as="blockquote">{item.description_with_HTML || <Trans>This item has no description yet. You can add one at the <BtnDesc icon="pencil-alt"/> from the Menu Items list.</Trans>}</Card.Text>
							</ListGroup.Item>
						</ListGroup>
						<ListGroup variant="flush">
							<ListGroup.Item className="overflow-hidden">
								<Form.Group className="d-flex w-100">
									<FileInputBtn.Pic block size="lg" jpg onSelectFile={onSelectFile}>{t('Select a new picture')}</FileInputBtn.Pic>
								</Form.Group>

								<Form.Group className="I-need-checkboxes-here">
									<Form.Check onClick={() => setTermsAccepted(!termsAccepted)} defaultChecked={false} id="terms"
									            label={<small><Trans>I am the owner of this image and I accept the <Link to="/terms">Terms of Service</Link>.</Trans></small>}/>
								</Form.Group>
							</ListGroup.Item>
						</ListGroup>

						<Card.Header className={!imgSrc && 'd-none'}><Icon icon="cut"/> {!cropped && imgSrc? t('Image cropper') : t('New image')}</Card.Header>
						<ListGroup className={(cropped || !imgSrc) && 'd-none'} variant="flush">
							<ListGroup.Item className="d-flex justify-content-center">
								{imgSrc && (
									<ReactCrop crop={crop} onChange={setCrop}>
										<img
											ref={imgRef}
											alt="Crop me"
											src={imgSrc}
											height={newHeight || 'auto'}
											width={newWidth || 'auto'}
											style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
										/>
									</ReactCrop>
								)}
							</ListGroup.Item>
							<ListGroup.Item>
								<Row>
									<Col className="cropper-btns">
										<ButtonGroup>
											<Btn icon="undo" variant="secondary" disabled={!imgSrc} onClick={() => setRotate(rotate - 10)}/>
											<Btn icon="redo" variant="secondary" disabled={!imgSrc} onClick={() => setRotate(rotate + 10)}/>
										</ButtonGroup>{' '}
										<ButtonGroup>
											<Btn icon="minus" variant="secondary" disabled={!imgSrc} onClick={() => setScale(scale * 0.8)}/>
											<Btn icon="plus" variant="secondary" disabled={!imgSrc} onClick={() => setScale(scale * 1.2)}/>
										</ButtonGroup>
									</Col>
									<Col xs="auto">
										<Btn icon="check" variant="success" onClick={doCrop}>{t('Crop')}</Btn>
									</Col>
								</Row>
							</ListGroup.Item>
						</ListGroup>
						<ListGroup className={!cropped && 'd-none'} variant="flush">
							<ListGroup.Item className="d-flex justify-content-center">
								<canvas
									ref={previewCanvasRef}
									style={{
										border: '1px solid black',
										width : IMAGE_RATIO,
										height: IMAGE_RATIO,
									}}
								/>
							</ListGroup.Item>
							<ListGroup.Item className="text-center">
								<Btn icon="trash-undo" swap variant="warning" onClick={() => setCropped(false)}>{t('Reset crop')}</Btn>
							</ListGroup.Item>
						</ListGroup>
					</Card>

					<Card>
						<Card.Header><Icon icon="image"/> {t('Current image')}</Card.Header>
						<ListGroup variant="flush">
							<ListGroup.Item className="text-center">
								{currentImage && !loading? //won't show the current image when uploading a new one
									<>
										<Form.Group>
											<img src={currentImage} className="mw-100" alt="item"/>
										</Form.Group>
										<Form.Group>
											<Btn icon="trash" swap variant="danger" size="lg" onClick={onDelete}>{t('Delete')}</Btn>
										</Form.Group>
									</>
									:
									<EmptyMessage icon="image" resource={API.RESOURCES.ITEMS} defaultMsg={t('Looks like there\'s no image for this item yet.')}/>
								}
							</ListGroup.Item>
						</ListGroup>
					</Card>
				</CardColumns>
			}
		</Screen.Content>

		<Screen.SaveFooter disabled={!(cropped && termsAccepted)} action={onSubmit}/>
	</Screen>
}
